import React, {useCallback, useEffect, useState} from "react";
import {Button, Form, Input, Modal, Select, Space, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {Landmark} from "lucide-react";
import {useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {RootState} from "../../redux/store";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";
import {HeroHeader, Card, SearchField, formatNaira, COLORS} from "../../designSystem";

const statusOptions = [
  {label: "All statuses", value: ""},
  {label: "Pending admin approval", value: "Validated"},
  {label: "Completed", value: "Completed"},
  {label: "Rejected", value: "Rejected"},
  {label: "Processing", value: "Processing"},
  {label: "Payout pending", value: "PayoutPending"},
];

const statusLabel = (value: any, row?: any) => row?.statusText || ({
  1: "Processing",
  2: "Completed",
  3: "Approved",
  4: "Rejected",
  5: "Payout Pending",
} as Record<number, string>)[Number(value)] || String(value || "-");

const statusColor = (value: any) => ({
  1: "gold",
  2: "green",
  3: "blue",
  4: "red",
  5: "purple",
} as Record<number, string>)[Number(value)] || "default";

const dateCell = (value: string) => value ? new Date(value).toLocaleString() : "-";
const moneyCell = (value: any) => <span className="font-mono">{formatNaira(value || 0)}</span>;

function CorporateWithdrawals() {
  const {token} = useSelector((state: RootState) => state.account);
  const [items, setItems] = useState<any[]>([]);
  const [pageInfo, setPageInfo] = useState({pageNumber: 1, pageSize: 10, totalItems: 0, totalPages: 1});
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState<any>("");
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [completing, setCompleting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async (pageNumber = 1) => {
    if (!token) return;
    setLoading(true);
    try {
      const query = buildQuery({searchParam, status, pageNumber, pageSize: pageInfo.pageSize});
      const payload = await adminRequest(`/corporate/withdrawal-requests${query}`, {token});
      const data = getReturnedObject(payload);
      const rows = data?.items || [];
      setItems(rows.map((row: any, index: number) => ({...row, key: row.id || index})));
      setPageInfo({
        pageNumber: data?.pageNumber || pageNumber,
        pageSize: data?.pageSize || pageInfo.pageSize,
        totalItems: data?.totalRecords || rows.length,
        totalPages: Math.max(1, Math.ceil((data?.totalRecords || rows.length) / (data?.pageSize || pageInfo.pageSize))),
      });
    } catch (error: any) {
      message.error(error?.message || "Unable to load corporate withdrawals");
    } finally {
      setLoading(false);
    }
  }, [token, searchParam, status, pageInfo.pageSize]);

  useEffect(() => {
    load(1);
  }, [load]);

  const completeWithdrawal = async () => {
    if (!token || !selectedRequest || completing) return;
    const values = await form.validateFields();
    setCompleting(true);
    try {
      await adminRequest(`/corporate/withdrawal-requests/${selectedRequest.id}/complete`, {
        token,
        method: "POST",
        body: JSON.stringify(values),
      });
      message.success("Corporate withdrawal completed");
      setSelectedRequest(null);
      form.resetFields();
      load(pageInfo.pageNumber);
    } catch (error: any) {
      Modal.error({
        title: "Unable to complete withdrawal",
        content: error?.message || "The payout could not be completed.",
      });
    } finally {
      setCompleting(false);
    }
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={Landmark}
          title="Corporate Withdrawals"
          subtitle="Review and complete corporate account withdrawal requests through the PayHive pool account"
        />

        <Card className="mt-8 p-5">
          <Space className="mb-4 flex flex-wrap" size={12}>
            <SearchField
              className="min-w-[280px] max-w-xl"
              placeholder="Search email or reference"
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
            />
            <Select
              className="w-64"
              options={statusOptions}
              value={status}
              onChange={setStatus}
            />
          </Space>

          <GenericTable
            loading={loading}
            data={items}
            pageInfo={pageInfo}
            handlePageChange={load}
            columns={[
              {title: "Corporate Account", dataIndex: "corporateAccountName", key: "corporateAccountName"},
              {title: "Amount", dataIndex: "amount", key: "amount", render: moneyCell},
              {title: "Destination", key: "destination", render: (_: any, row: any) => `${row.accountName || "-"} · ${row.accountNumber || "-"} · ${row.bank || "-"}`},
              {title: "Reference", dataIndex: "transactionReference", key: "transactionReference"},
              {title: "External Ref", dataIndex: "externalReference", key: "externalReference", render: (value: string) => value || "-"},
              {title: "Status", dataIndex: "status", key: "status", render: (value: any, row: any) => <Tag color={statusColor(value)}>{statusLabel(value, row)}</Tag>},
              {title: "Requested", dataIndex: "dateCreated", key: "dateCreated", render: dateCell},
              {
                title: "Action",
                key: "action",
                render: (_: any, row: any) => Number(row.status) === 3 ? (
                  <Button size="small" type="primary" onClick={() => setSelectedRequest(row)}>
                    Complete payout
                  </Button>
                ) : "-",
              },
            ]}
          />
        </Card>

        <Modal
          title="Complete Corporate Withdrawal"
          open={!!selectedRequest}
          onOk={completeWithdrawal}
          onCancel={() => {
            if (!completing) setSelectedRequest(null);
          }}
          okText={completing ? "Payout pending..." : "Complete payout"}
          confirmLoading={completing}
          okButtonProps={{disabled: completing}}
          cancelButtonProps={{disabled: completing}}
        >
          <Form form={form} layout="vertical">
            <Form.Item label="External Reference" name="externalReference">
              <Input placeholder="Optional VFD/bank reference"/>
            </Form.Item>
            <Form.Item label="Payment Proof URL" name="paymentProofUrl">
              <Input placeholder="Optional proof URL"/>
            </Form.Item>
            <Form.Item label="Narration" name="narration">
              <Input.TextArea rows={3} placeholder="Optional payout note"/>
            </Form.Item>
          </Form>
        </Modal>
      </main>
    </AdminLayout>
  );
}

export default CorporateWithdrawals;
